import { ReactNode, useState } from 'react';

import {
  AngleDown,
  AngleUp,
  ChakraBadge,
  ChakraButton,
  ChakraCollapse,
  ChakraFlex,
  ChakraGridItem,
  ChakraSimpleGrid,
  ChakraText,
  EquipUITheme,
  InfiniteScroll,
} from '@equip.health/ui';

import { SVGElementType } from '~/lib/constants/task.constants';
import { TaskEvents } from '~/analytics/taskEvents';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';

const { caption, captionBold, h4 } = EquipUITheme.typography;

export interface TaskListProps {
  data: {
    colorScheme?: string;
    count: number;
    items: ReactNode[];
    key: string;
    title: string;
  }[];
  fetchNextBatch: () => void;
  height?: string;
  hideSectionHeaders: boolean;
  totalTasksCount: number;
}

type TaskGroupProps = {
  count: number;
  hideTitleAndToggle: boolean;
  id: string;
  title: string;
  items: ReactNode[];
};

const TaskGroup = ({
  count,
  hideTitleAndToggle,
  id,
  title,
  items,
}: TaskGroupProps) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const { trackEvent } = useAnalytics();

  const handleToggle = (): void => {
    const event = isExpanded
      ? TaskEvents.collapseTaskGroupFromTasksListPanel
      : TaskEvents.expandTaskGroupFromTasksListPanel;

    trackEvent(event, {
      groupName: title,
      isExpanded: !isExpanded,  
      Source: AnalyticsSourceValues.TASKS_LIST_PANEL,
    });
    setIsExpanded(!isExpanded);
  };
  const ToggleIcon: SVGElementType = isExpanded ? AngleUp : AngleDown;

  const TOP_LEVEL_ID = `task-list-section__${id}`;

  return (
    <>
      {!hideTitleAndToggle && (
        <ChakraSimpleGrid
          paddingBottom="16px"
          paddingTop="28px"
          templateColumns="repeat(2, 1fr)"
        >
          <ChakraGridItem alignItems="center" colSpan={1} display="flex">
            <ChakraText
              {...h4}
              color="neutral.primary"
              id={`${TOP_LEVEL_ID}__title`}
            >
              {title}
            </ChakraText>
            <ChakraFlex
              alignItems="center"
              backgroundColor="neutral.tertiary"
              borderRadius="100px"
              color="white"
              fontSize="10px"
              justifyContent="center"
              marginLeft="10px"
              padding="2px 8px"
            >
              <ChakraBadge
                {...caption}
                backgroundColor="transparent"
                color="white"
                id={`${TOP_LEVEL_ID}__count`}
                textTransform="none"
              >
                <ChakraText as="span" {...captionBold}>
                  {count}
                </ChakraText>
                <ChakraText as="span" {...caption}>{` task${
                  count !== 1 ? 's' : ''
                }`}</ChakraText>
              </ChakraBadge>
            </ChakraFlex>
          </ChakraGridItem>
          <ChakraGridItem alignItems="end" colEnd={3} colStart={3}>
            <ChakraButton
              {...captionBold}
              _hover={{ background: 'transparent' }}
              background="transparent"
              color="neutral.tertiary"
              fontWeight="700"
              id={`${TOP_LEVEL_ID}__toggle`}
              onClick={handleToggle}
              rightIcon={<ToggleIcon height="6px" width="10px" />}
              size="xs"
            >
              {isExpanded ? 'Collapse' : 'Expand'}
            </ChakraButton>
          </ChakraGridItem>
        </ChakraSimpleGrid>
      )}
      <ChakraCollapse in={isExpanded}>
        <ChakraSimpleGrid
          gap="8px"
          paddingTop={hideTitleAndToggle ? '16px' : 0}
        >
          {items}
        </ChakraSimpleGrid>
      </ChakraCollapse>
    </>
  );
};

const TaskList = ({
  data,
  fetchNextBatch,
  height,
  hideSectionHeaders,
  totalTasksCount,
}: TaskListProps) => {
  const dataLength = data.reduce((sum, group) => sum + group.items.length, 0);
  return (
    <InfiniteScroll
      dataLength={dataLength}
      fetchData={fetchNextBatch}
      height={height}
      id="task-list"
      totalCount={totalTasksCount}
    >
      {(data ?? []).map(({ count, items, key, title }) => (
        <ChakraGridItem key={`${key}-section`} paddingBottom="8px">
          <TaskGroup
            count={count}
            hideTitleAndToggle={hideSectionHeaders}
            id={key}
            items={items}
            title={title}
          />
        </ChakraGridItem>
      ))}
    </InfiniteScroll>
  );
};

export default TaskList;
